import { Autocomplete } from "@mui/material";
import { FormikValues, getIn } from "formik";

import { filterDropdownOptions } from "../../utils/filterDropdownOptions";
import { getFieldPath } from "../FormikField.utils";
import { FormikSystemDropdownAutocompleteProps, IOptionItem } from "./FormikAutocomplete.types";
import { FormikAutocompleteTextField } from "./components/FormikAutocompleteTextField";

/**
 * A system-relevant dropdown that still accepts free text.
 *
 * Picking an entry stores its stable key while the input shows the label; typing anything else stores
 * the text verbatim, which keeps addresses outside the configured lists enterable. Typed text that
 * exactly matches a configured label is stored as that entry's key, so hitting the list by hand is
 * equivalent to selecting from it. Options marked {@link IOptionItem#isHidden} still resolve a stored
 * key to its label but are left out of the list.
 */
export function FormikSystemDropdownAutocomplete<T extends FormikValues>(
  props: FormikSystemDropdownAutocompleteProps<T>
) {
  const {
    fieldName,
    path,
    textFieldProps,
    formik,
    helperText,
    label,
    options,
    required,
    optional,
    onValueChange,
    ...others
  } = props;

  const fullPath = getFieldPath(path, fieldName);
  const stored = (getIn(formik.values, fullPath) as string | null | undefined) ?? "";
  const matched = options.find((option) => option.id === stored);

  // A stored value with no configured entry is free text, so it is its own label.
  const displayValue = matched?.label ?? stored;

  const storeText = (text: string) => {
    const matchedLabel = options.find((option) => option.label === text);
    const storedValue = matchedLabel?.id ?? text;
    void formik.setFieldValue(fullPath, storedValue);
    onValueChange?.(storedValue);
  };

  return (
    <Autocomplete<IOptionItem, false, false, true>
      freeSolo
      value={matched ?? null}
      inputValue={displayValue}
      onChange={(_, newValue, reason) => {
        if (reason === "clear" || newValue === null) {
          void formik.setFieldValue(fullPath, "");
          onValueChange?.(null);
          return;
        }

        if (typeof newValue === "string") {
          storeText(newValue);
          return;
        }

        void formik.setFieldValue(fullPath, newValue.id);
        onValueChange?.(newValue.id);
      }}
      onInputChange={(_, newInput, reason) => {
        if (reason === "input") {
          storeText(newInput);
        }
      }}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      getOptionLabel={(option) => (typeof option === "string" ? option : option.label)}
      options={options}
      filterOptions={(opts, state) => filterDropdownOptions(opts, state).filter((option) => !option.isHidden)}
      renderInput={(params) => (
        <FormikAutocompleteTextField
          params={params}
          textFieldProps={textFieldProps}
          formik={formik}
          fieldPath={fullPath}
          helperText={helperText}
          label={label}
          required={required}
          optional={optional}
        />
      )}
      fullWidth
      {...others}
    />
  );
}
